/**
 * Assignment Panel Component
 * Allows instructors to assign quizzes to students
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiUsers, FiCheckCircle, FiArrowLeft, FiClock, FiAlertCircle } from 'react-icons/fi';
import quizApiService from '../../services/quizApi';

const AssignmentsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem 3rem;
  background-color: var(--background-secondary);
  min-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  @media (max-width: 768px) {
    padding: 1rem 0.75rem 2rem;
  }
`;

const Header = styled.div`
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border: 1px solid rgba(216, 184, 137, 0.22);
  border-radius: 999px;
  background-color: var(--background-primary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: var(--transition);
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  white-space: nowrap;
  position: relative;
  overflow: hidden;
  
  &:hover {
    background-color: var(--background-tertiary);
    color: var(--text-primary);
    border-color: var(--primary-color);
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
  }
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: none;
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-size: clamp(1.5rem, 2.2vw, 2rem);
  font-weight: 800;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  margin: 0;
`;

const AssignmentCard = styled(motion.div)`
  background: var(--background-primary);
  border: 1px solid rgba(216, 184, 137, 0.18);
  border-radius: 24px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  position: relative;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: rgba(216, 184, 137, 0.32);
    box-shadow: 0 20px 50px rgba(15, 23, 42, 0.08);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    border-radius: 18px;
    margin-bottom: 1rem;
  }
`;

const AssignmentHeader = styled.div`
  padding: 1.5rem 2rem;
  background: #F7F5F1;
  border-bottom: 1px solid rgba(216, 184, 137, 0.18);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    flex-direction: column;
    align-items: stretch;
  }
`;

const AssignmentInfo = styled.div`
  flex: 1;
`;

const AssignmentTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
  letter-spacing: -0.025em;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 0.375rem;
  }
`;

const AssignmentSubtitle = styled.p`
  color: var(--text-secondary);
  font-size: 0.95rem;
  margin: 0;
  line-height: 1.5;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const AssignmentContent = styled.div`
  padding: 2rem;
  background: var(--background-primary);

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const FormSection = styled.section`
  padding-bottom: 1.75rem;
  margin-bottom: 1.75rem;
  border-bottom: 1px solid rgba(216, 184, 137, 0.16);

  &:last-of-type {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const SectionTitle = styled.h4`
  margin: 0 0 1rem;
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -0.015em;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.875rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;


const DateTimeInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  min-height: 48px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 16px;
  font-size: 0.875rem;
  background: rgba(255, 255, 255, 0.95);
  transition: var(--transition);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: var(--text-primary);
  position: relative;
  cursor: pointer;
  
  &::-webkit-calendar-picker-indicator {
    background: transparent;
    bottom: 0;
    color: transparent;
    cursor: pointer;
    height: auto;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: auto;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3e%3cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'/%3e%3c/svg%3e");
    background-size: 20px 20px;
    background-repeat: no-repeat;
    background-position: right 12px center;
    padding-right: 40px;
  }

  &:hover {
    border-color: var(--primary-color);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background: var(--background-primary);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
    font-style: italic;
  }

  /* Firefox */
  &::-moz-calendar-picker-indicator {
    background: transparent;
    color: transparent;
    cursor: pointer;
    height: 20px;
    width: 20px;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3e%3cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'/%3e%3c/svg%3e");
    background-size: 20px 20px;
    background-repeat: no-repeat;
    background-position: center;
    margin-right: 8px;
  }
`;

const CustomInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  min-height: 48px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 16px;
  font-size: 0.875rem;
  background: rgba(255, 255, 255, 0.95);
  transition: var(--transition);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: var(--text-primary);

  &:hover {
    border-color: var(--primary-color);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background: var(--background-primary);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
    font-style: italic;
  }
`;

const TimePickerContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: end;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TimePickerGroup = styled.div`
  flex: 1;
`;

const CustomOptionContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: end;
  margin-top: 0.5rem;
`;

const CustomOptionGroup = styled.div`
  flex: 1;
`;


const Select = styled.select`
  width: 100%;
  padding: 0.75rem 3rem 0.75rem 1rem;
  min-height: 48px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 16px;
  font-size: 0.875rem;
  background-color: rgba(255, 255, 255, 0.95);
  cursor: pointer;
  transition: var(--transition);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, var(--text-secondary) 50%), linear-gradient(135deg, var(--text-secondary) 50%, transparent 50%);
  background-position: calc(100% - 27px) 50%, calc(100% - 20px) 50%;
  background-size: 7px 7px, 7px 7px;
  background-repeat: no-repeat;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: var(--primary-color);
`;

const HelperText = styled.div`
  font-size: 0.78rem;
  color: var(--text-muted);
  margin-top: 0.4rem;
  font-style: normal;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const FieldErrorText = styled(HelperText)`
  color: var(--error-color);
  font-weight: 600;
`;

const InlineTextButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: var(--primary-color);
  font-size: 0.78rem;
  font-weight: 600;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover {
    color: var(--primary-dark);
  }

  &:disabled {
    color: var(--text-muted);
    cursor: not-allowed;
    text-decoration-color: transparent;
    opacity: 0.6;
  }
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;

  ${Label} {
    margin-bottom: 0;
  }
`;

const EmptyStudentsState = styled.div`
  padding: 1rem;
  text-align: center;
  color: var(--text-muted);
  font-style: italic;
  background-color: #F7F5F1;
  border-radius: 16px;
  border: 1px dashed rgba(216, 184, 137, 0.35);
`;

const ToggleRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  cursor: pointer;
`;

const ToggleInput = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
`;

const ToggleTrack = styled.span`
  width: 46px;
  height: 26px;
  border-radius: 999px;
  background: ${props => props.$checked ? 'var(--primary-color)' : 'rgba(15, 23, 42, 0.14)'};
  position: relative;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    top: 3px;
    left: ${props => props.$checked ? '23px' : '3px'};
    border-radius: 999px;
    background: white;
    box-shadow: 0 1px 4px rgba(15, 23, 42, 0.2);
    transition: all 0.2s ease;
  }
`;

const ToggleText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ToggleLabel = styled.span`
  color: var(--text-primary);
  font-size: 0.95rem;
  font-weight: 600;
`;

const ErrorNotice = styled.div`
  background: rgba(252, 129, 129, 0.1);
  color: var(--error-color);
  padding: 1rem;
  border-radius: 16px;
  margin-bottom: 1rem;
  border: 1px solid rgba(252, 129, 129, 0.35);
`;

const StudentList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid rgba(216, 184, 137, 0.18);
  border-radius: 16px;
  padding: 1rem;
  background: #F7F5F1;
`;

const StudentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--background-primary);
  }
`;

const StudentInfo = styled.div`
  flex: 1;
`;

const StudentName = styled.div`
  font-weight: 500;
  color: var(--text-primary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const StudentEmail = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const AssignedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  margin-left: 0.5rem;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #15803d;
  background: rgba(22, 163, 74, 0.12);
  border: 1px solid rgba(22, 163, 74, 0.28);
  border-radius: 999px;
`;

const NewBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  margin-left: 0.5rem;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #b45309;
  background: rgba(180, 83, 9, 0.12);
  border: 1px solid rgba(180, 83, 9, 0.28);
  border-radius: 999px;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const FilterPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 4px 12px;
  background: ${(p) => (p.$active ? 'rgba(184, 149, 105, 0.18)' : 'transparent')};
  border: 1px solid ${(p) => (p.$active ? 'var(--primary-color, #B89569)' : 'var(--border-color, #e6e1da)')};
  border-radius: 999px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${(p) => (p.$active ? 'var(--primary-color, #B89569)' : 'var(--text-secondary)')};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--primary-color, #B89569);
    color: var(--primary-color, #B89569);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(216, 184, 137, 0.18);

  @media (max-width: 768px) {
    gap: 0.5rem;
    margin-top: 0.75rem;
    padding-top: 0.75rem;
  }
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.25rem;
  border: 1px solid transparent;
  border-radius: 999px;
  background-color: var(--background-secondary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  white-space: nowrap;
  position: relative;
  overflow: hidden;
  
  &:hover {
    background-color: var(--background-tertiary);
    color: var(--text-primary);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }
  
  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);
    font-weight: 600;
    box-shadow: 0 4px 12px rgba(184, 149, 105, 0.18);
    
    &:hover {
      background: var(--primary-dark);
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(184, 149, 105, 0.24);
    }
  }
  
  &.secondary {
    background: var(--background-primary);
    color: var(--text-secondary);
    border-color: rgba(216, 184, 137, 0.24);
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
    
    &:hover {
      background: #F7F5F1;
      color: var(--text-primary);
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }

  @media (max-width: 768px) {
    padding: 0.625rem 1rem;
    font-size: 0.8125rem;
    flex: 1;
    justify-content: center;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-muted);
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--danger-color);
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 1rem;
  }
`;

const SuccessState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--success-color);
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 1rem;
  }
`;

function AssignmentPanel() {
  const { quizId } = useParams();
  const navigate = useNavigate();

  const [quiz, setQuiz] = useState(null);
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  // Filter the visible roster so the instructor can focus on newly-enrolled
  // students. Values: 'all' | 'unassigned' | 'assigned'. Only meaningful when
  // every row has the is_assigned flag (i.e. quiz id was passed in the fetch).
  const [studentFilter, setStudentFilter] = useState('all');
  const [assignmentData, setAssignmentData] = useState({
    course_id: '',
    due_date: '',
    due_time: '',
    due_at: '',
    attempts_allowed: 1,
    time_limit_minutes: null,
    immediate_feedback: true
  });
  const [customTimeLimit, setCustomTimeLimit] = useState('');
  const [customAttempts, setCustomAttempts] = useState('');
  const [useCustomTimeLimit, setUseCustomTimeLimit] = useState(false);
  const [useCustomAttempts, setUseCustomAttempts] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const dueTimeRequired = Boolean(assignmentData.due_date && !assignmentData.due_time);
  const dueDateRequired = Boolean(assignmentData.due_time && !assignmentData.due_date);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch quiz and courses data
      const [quizResponse, coursesResponse] = await Promise.all([
        quizApiService.getQuiz(quizId),
        quizApiService.getCourses()
      ]);
      
      if (!quizResponse.success) {
        setError('Failed to fetch quiz');
        return;
      }
      
      if (!coursesResponse.success) {
        setError('Failed to fetch courses');
        return;
      }
      
      setQuiz(quizResponse.data);
      const allCourses = coursesResponse.courses || coursesResponse.data || [];
      // Filter to only show active courses that the instructor teaches
      const activeCourses = allCourses.filter(course => 
        course.is_active === 1 || course.is_active === true
      );
      setCourses(activeCourses);
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [quizId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchStudentsForCourse = useCallback(async (courseId) => {
    if (!courseId) {
      setStudents([]);
      return;
    }

    try {
      // Pass the current quiz id so the API can mark each student with an
      // is_assigned flag — students already assigned this quiz are pre-checked
      // and visually labelled "Assigned"; newly enrolled (unassigned) students
      // show up unchecked so they can be opted in here.
      const studentsResponse = await quizApiService.getStudentsByCourse(courseId, quiz?.id);

      if (studentsResponse.success) {
        const list = studentsResponse.data || [];
        setStudents(list);
        const preselected = list.filter(s => s.is_assigned).map(s => s.id);
        setSelectedStudents(preselected);
      } else {
        setError('Failed to fetch students for this course');
        setStudents([]);
      }
    } catch (err) {
      console.error('Error fetching students:', err);
      setError('Failed to fetch students for this course');
      setStudents([]);
    }
  }, [quiz?.id]);

  const handleStudentToggle = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  // Roster the instructor currently sees, after applying the filter pill.
  // Keeps the underlying `students` array intact so we can compute totals
  // and badges regardless of what's filtered.
  const hasAssignedFlag = students.length > 0 && typeof students[0]?.is_assigned === 'boolean';
  const visibleStudents = students.filter(s => {
    if (!hasAssignedFlag) return true;
    if (studentFilter === 'unassigned') return s.is_assigned === false;
    if (studentFilter === 'assigned') return s.is_assigned === true;
    return true;
  });
  const visibleSelectedCount = visibleStudents.filter(s => selectedStudents.includes(s.id)).length;
  const allVisibleSelected = visibleStudents.length > 0 && visibleSelectedCount === visibleStudents.length;

  // Select-All now operates on the currently-visible filtered list so the
  // instructor can e.g. switch to "Not assigned" and tick every newcomer in
  // one click without disturbing existing assignees.
  const handleSelectAll = () => {
    if (allVisibleSelected) {
      const visibleIds = new Set(visibleStudents.map(s => s.id));
      setSelectedStudents(prev => prev.filter(id => !visibleIds.has(id)));
    } else {
      const visibleIds = visibleStudents.map(s => s.id);
      setSelectedStudents(prev => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const unassignedCount = hasAssignedFlag ? students.filter(s => s.is_assigned === false).length : 0;
  const assignedCount = hasAssignedFlag ? students.filter(s => s.is_assigned === true).length : 0;

  const handleAssignmentChange = (field, value) => {
    setAssignmentData(prev => ({ ...prev, [field]: value }));
    
    // If course is changed, fetch students for that course
    if (field === 'course_id') {
      fetchStudentsForCourse(value);
    }
  };

  const setDueDateToToday = () => {
    const today = new Date().toLocaleDateString('en-CA');
    setAssignmentData(prev => ({
      ...prev,
      due_date: today,
      due_at: prev.due_time ? new Date(`${today}T${prev.due_time}`).toISOString() : ''
    }));
  };

  const setDueDateToTomorrow = () => {
    const tomorrowDate = new Date();
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    const tomorrow = tomorrowDate.toLocaleDateString('en-CA');
    setAssignmentData(prev => ({
      ...prev,
      due_date: tomorrow,
      due_at: prev.due_time ? new Date(`${tomorrow}T${prev.due_time}`).toISOString() : ''
    }));
  };

  const handleCustomTimeLimitChange = (value) => {
    setCustomTimeLimit(value);
    if (value && !isNaN(value) && value > 0) {
      setAssignmentData(prev => ({ ...prev, time_limit_minutes: parseInt(value) }));
    }
  };

  const handleCustomAttemptsChange = (value) => {
    setCustomAttempts(value);
    if (value && !isNaN(value) && value > 0) {
      setAssignmentData(prev => ({ ...prev, attempts_allowed: parseInt(value) }));
    }
  };

  const handleTimeLimitSelectChange = (value) => {
    if (value === 'custom') {
      setUseCustomTimeLimit(true);
    } else {
      setUseCustomTimeLimit(false);
      setCustomTimeLimit('');
      setAssignmentData(prev => ({ ...prev, time_limit_minutes: value ? parseInt(value) : null }));
    }
  };

  const setTimeLimitShortcut = (minutes) => {
    setUseCustomTimeLimit(false);
    setCustomTimeLimit('');
    setAssignmentData(prev => ({ ...prev, time_limit_minutes: minutes }));
  };

  const handleAttemptsSelectChange = (value) => {
    if (value === 'custom') {
      setUseCustomAttempts(true);
    } else {
      setUseCustomAttempts(false);
      setCustomAttempts('');
      setAssignmentData(prev => ({ ...prev, attempts_allowed: parseInt(value) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!assignmentData.course_id) {
      setError('Please select a course');
      return;
    }
    
    if (selectedStudents.length === 0) {
      setError('Please select at least one student');
      return;
    }

    if (dueTimeRequired) {
      setError('Please select a time for the due date');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const response = await quizApiService.assignQuiz(quizId, {
        course_id: assignmentData.course_id,
        studentIds: selectedStudents,
        due_at: assignmentData.due_date && assignmentData.due_time ? new Date(`${assignmentData.due_date}T${assignmentData.due_time}`).toISOString() : null,
        attempts_allowed: assignmentData.attempts_allowed,
        time_limit_minutes: assignmentData.time_limit_minutes || null,
        immediate_feedback: assignmentData.immediate_feedback
      });

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/instructor/quizzes');
        }, 2000);
      } else {
        setError('Failed to assign quiz');
      }
    } catch (err) {
      console.error('Error assigning quiz:', err);
      setError('Failed to assign quiz');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AssignmentsContainer>
        <Header>
          <BackButton onClick={() => navigate('/instructor/quizzes')}>
            <FiArrowLeft size={16} />
            Back to Quizzes
          </BackButton>
          <Title>Assign Quiz</Title>
        </Header>
        <LoadingState>
          <FiClock size={48} />
          <h3>Loading...</h3>
          <p>Please wait while we fetch the quiz and student data.</p>
        </LoadingState>
      </AssignmentsContainer>
    );
  }

  if (error && !success) {
    return (
      <AssignmentsContainer>
        <Header>
          <BackButton onClick={() => navigate('/instructor/quizzes')}>
            <FiArrowLeft size={16} />
            Back to Quizzes
          </BackButton>
          <Title>Assign Quiz</Title>
        </Header>
        <ErrorState>
          <FiAlertCircle size={48} />
          <h3>Error loading data</h3>
          <p>{error}</p>
        </ErrorState>
      </AssignmentsContainer>
    );
  }

  if (success) {
    return (
      <AssignmentsContainer>
        <Header>
          <BackButton onClick={() => navigate('/instructor/quizzes')}>
            <FiArrowLeft size={16} />
            Back to Quizzes
          </BackButton>
          <Title>Assign Quiz</Title>
        </Header>
        <SuccessState>
          <FiCheckCircle size={48} />
          <h3>Quiz Assigned Successfully!</h3>
          <p>Redirecting to quiz list...</p>
        </SuccessState>
      </AssignmentsContainer>
    );
  }

  return (
    <AssignmentsContainer>
      <Header>
        <BackButton onClick={() => navigate('/instructor/quizzes')}>
          <FiArrowLeft size={16} />
          Back to Quizzes
        </BackButton>
      </Header>

      <AssignmentCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <AssignmentHeader>
          <AssignmentInfo>
            <AssignmentTitle>Quiz Assignment</AssignmentTitle>
            <AssignmentSubtitle>Assign “{quiz?.title}” to students</AssignmentSubtitle>
          </AssignmentInfo>
        </AssignmentHeader>
        
        <AssignmentContent>
          <form onSubmit={handleSubmit}>
            <FormSection>
              <SectionTitle>Assignment Setup</SectionTitle>
              <FormGroup>
                <Label htmlFor="course_id">Course *</Label>
                <Select
                  id="course_id"
                  value={assignmentData.course_id}
                  onChange={(e) => handleAssignmentChange('course_id', e.target.value)}
                  required
                >
                  <option value="">Choose a course...</option>
                  {courses && courses.map(course => (
                    <option key={course.id} value={course.id}>
                      {course.name} ({course.code})
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>
                  Students {assignmentData.course_id && courses ? `from ${courses.find(c => c.id === assignmentData.course_id)?.name || 'selected course'}` : ''} *
                </Label>
                {assignmentData.course_id ? (
                  <>
                    {hasAssignedFlag && (
                      <FilterRow>
                        <FilterPill
                          type="button"
                          $active={studentFilter === 'all'}
                          onClick={() => setStudentFilter('all')}
                        >
                          All ({students.length})
                        </FilterPill>
                        <FilterPill
                          type="button"
                          $active={studentFilter === 'unassigned'}
                          onClick={() => setStudentFilter('unassigned')}
                        >
                          Not assigned ({unassignedCount})
                        </FilterPill>
                        <FilterPill
                          type="button"
                          $active={studentFilter === 'assigned'}
                          onClick={() => setStudentFilter('assigned')}
                        >
                          Assigned ({assignedCount})
                        </FilterPill>
                      </FilterRow>
                    )}
                    <CheckboxGroup>
                      <Checkbox
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={handleSelectAll}
                      />
                      <Label>
                        {hasAssignedFlag && studentFilter === 'unassigned'
                          ? `Select all not assigned (${visibleStudents.length})`
                          : hasAssignedFlag && studentFilter === 'assigned'
                            ? `Select all assigned (${visibleStudents.length})`
                            : `Select All (${visibleStudents.length} students)`}
                      </Label>
                    </CheckboxGroup>
                    <StudentList>
                      {visibleStudents.length === 0 ? (
                        <EmptyStudentsState>
                          {hasAssignedFlag && studentFilter === 'unassigned'
                            ? 'Everyone in this course is already assigned this quiz.'
                            : hasAssignedFlag && studentFilter === 'assigned'
                              ? 'No students have been assigned this quiz yet.'
                              : 'No students enrolled in this course.'}
                        </EmptyStudentsState>
                      ) : visibleStudents.map(student => (
                        <StudentItem key={student.id}>
                          <Checkbox
                            type="checkbox"
                            checked={selectedStudents.includes(student.id)}
                            onChange={() => handleStudentToggle(student.id)}
                          />
                          <StudentInfo>
                            <StudentName>
                              {student.name}
                              {/* Only show badges when we actually have the
                                  flag (i.e. when a quiz id was provided in the
                                  fetch). Avoids labelling everyone "Not
                                  assigned" in the initial create-assignment
                                  flow where there's no existing assignment yet. */}
                              {student.is_assigned === true && (
                                <AssignedBadge>Assigned</AssignedBadge>
                              )}
                              {student.is_assigned === false && (
                                <NewBadge>Not assigned</NewBadge>
                              )}
                            </StudentName>
                            <StudentEmail>{student.email}</StudentEmail>
                          </StudentInfo>
                        </StudentItem>
                      ))}
                    </StudentList>
                  </>
                ) : (
                  <EmptyStudentsState>Select a course to view available students.</EmptyStudentsState>
                )}
              </FormGroup>
            </FormSection>

            <FormSection>
              <SectionTitle>Scheduling</SectionTitle>
              <FormGroup>
                <LabelRow>
                  <Label htmlFor="due_at">Due Date & Time</Label>
                  <InlineTextButton type="button" onClick={setDueDateToToday}>
                    Set to today
                  </InlineTextButton>
                  <InlineTextButton type="button" onClick={setDueDateToTomorrow}>
                    Set to tomorrow
                  </InlineTextButton>
                </LabelRow>
                <TimePickerContainer>
                  <TimePickerGroup>
                    <DateTimeInput
                      id="due_date"
                      type="date"
                      value={assignmentData.due_date}
                      onChange={(e) => {
                        const dateValue = e.target.value;
                        setAssignmentData(prev => ({
                          ...prev,
                          due_date: dateValue,
                          due_at: dateValue && prev.due_time ? new Date(`${dateValue}T${prev.due_time}`).toISOString() : ''
                        }));
                      }}
                    />
                  </TimePickerGroup>
                  <TimePickerGroup>
                    <DateTimeInput
                      id="due_time"
                      type="time"
                      required={Boolean(assignmentData.due_date)}
                      aria-invalid={dueTimeRequired}
                      value={assignmentData.due_time}
                      onChange={(e) => {
                        const timeValue = e.target.value;
                        setAssignmentData(prev => ({
                          ...prev,
                          due_time: timeValue,
                          due_at: prev.due_date && timeValue ? new Date(`${prev.due_date}T${timeValue}`).toISOString() : ''
                        }));
                      }}
                    />
                  </TimePickerGroup>
                </TimePickerContainer>
                {dueDateRequired ? (
                  <FieldErrorText>Select a due date before setting a time</FieldErrorText>
                ) : dueTimeRequired ? (
                  <FieldErrorText>Select a time for the due date</FieldErrorText>
                ) : (
                  <HelperText>Optional deadline for quiz access</HelperText>
                )}
              </FormGroup>

              <FieldGrid>
                <FormGroup>
                  <LabelRow>
                    <Label htmlFor="time_limit_minutes">Time Limit</Label>
                    <InlineTextButton type="button" onClick={() => setTimeLimitShortcut(30)}>
                      30 minutes
                    </InlineTextButton>
                    <InlineTextButton type="button" onClick={() => setTimeLimitShortcut(60)}>
                      60 minutes
                    </InlineTextButton>
                    <InlineTextButton type="button" onClick={() => setTimeLimitShortcut(90)}>
                      90 minutes
                    </InlineTextButton>
                    <InlineTextButton type="button" onClick={() => setTimeLimitShortcut(120)}>
                      120 minutes
                    </InlineTextButton>
                  </LabelRow>
                  <Select
                    id="time_limit_minutes"
                    value={useCustomTimeLimit ? 'custom' : (assignmentData.time_limit_minutes || '')}
                    onChange={(e) => handleTimeLimitSelectChange(e.target.value)}
                  >
                    <option value="">No time limit</option>
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="45">45 minutes</option>
                    <option value="60">1 hour</option>
                    <option value="90">1.5 hours</option>
                    <option value="120">2 hours</option>
                    <option value="180">3 hours</option>
                    <option value="240">4 hours</option>
                    <option value="300">5 hours</option>
                    <option value="custom">Other (specify below)</option>
                  </Select>
                  {useCustomTimeLimit && (
                    <CustomOptionContainer>
                      <CustomOptionGroup>
                        <CustomInput
                          type="number"
                          placeholder="Enter minutes"
                          value={customTimeLimit}
                          onChange={(e) => handleCustomTimeLimitChange(e.target.value)}
                          min="1"
                          max="1440"
                        />
                      </CustomOptionGroup>
                    </CustomOptionContainer>
                  )}
                  <HelperText>Maximum time students can spend on the quiz</HelperText>
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="attempts_allowed">Attempts Allowed</Label>
                  <Select
                    id="attempts_allowed"
                    value={useCustomAttempts ? 'custom' : assignmentData.attempts_allowed}
                    onChange={(e) => handleAttemptsSelectChange(e.target.value)}
                  >
                    <option value={1}>1 attempt</option>
                    <option value={2}>2 attempts</option>
                    <option value={3}>3 attempts</option>
                    <option value={5}>5 attempts</option>
                    <option value={10}>10 attempts</option>
                    <option value={15}>15 attempts</option>
                    <option value={20}>20 attempts</option>
                    <option value={50}>50 attempts</option>
                    <option value={100}>100 attempts</option>
                    <option value="custom">Other (specify below)</option>
                  </Select>
                  {useCustomAttempts && (
                    <CustomOptionContainer>
                      <CustomOptionGroup>
                        <CustomInput
                          type="number"
                          placeholder="Enter attempts"
                          value={customAttempts}
                          onChange={(e) => handleCustomAttemptsChange(e.target.value)}
                          min="1"
                          max="999"
                        />
                      </CustomOptionGroup>
                    </CustomOptionContainer>
                  )}
                  <HelperText>Number of attempts allowed</HelperText>
                </FormGroup>
              </FieldGrid>
            </FormSection>

            <FormSection>
              <SectionTitle>Submission Settings</SectionTitle>
              <ToggleRow htmlFor="immediate_feedback">
                <ToggleInput
                  id="immediate_feedback"
                  type="checkbox"
                  checked={assignmentData.immediate_feedback}
                  onChange={(e) => handleAssignmentChange('immediate_feedback', e.target.checked)}
                />
                <ToggleTrack $checked={assignmentData.immediate_feedback} />
                <ToggleText>
                  <ToggleLabel>Show results immediately to student after quiz submission</ToggleLabel>
                </ToggleText>
              </ToggleRow>
            </FormSection>

            {error && (
              <ErrorNotice>
                {error}
              </ErrorNotice>
            )}

            <ButtonGroup>
              <ActionButton 
                type="button" 
                className="secondary" 
                onClick={() => navigate('/instructor/quizzes')}
              >
                Cancel
              </ActionButton>
              <ActionButton 
                type="submit" 
                className="primary" 
                disabled={saving || selectedStudents.length === 0}
              >
                <FiUsers size={16} />
                {saving ? 'Assigning...' : `Assign to ${selectedStudents.length} Students`}
              </ActionButton>
            </ButtonGroup>
          </form>
        </AssignmentContent>
      </AssignmentCard>
    </AssignmentsContainer>
  );
}

export default AssignmentPanel;
